import { ContentNugget } from '@backrs_ui/common-ui';

import {
  SEARCH_TYPES,
  searchForContentNuggets,
  SearchResult,
  setSearchLoading,
} from '@backrs_ui/grow-ui/src/redux/search/search';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';

import { PageButtonProps } from './components/Pagination';
import {
  ALL_SEARCH_CATEGORIES,
  SEARCH_CATEGORIES_INTERNSHIPS,
  SEARCH_CATEGORIES_SCHOLARSHIPS,
} from './search.filters';
import {
  careerPathTransform,
  competencyTransform,
  internshipTransform,
  profileTransform,
  scholarshipTransform,
  externalProfileTransform,
  collegeTransform,
} from './search.nuggetTransformers';
import { SEARCH_PAGE_CATEGORIES, SearchCategory, SearchPageCategory } from './search.types';

export const fetchNuggetsUserHasSearched = async (
  dispatch: AppDispatch,
  search_term: string,
  page: number,
  types?: SearchCategory[],
  category: SearchPageCategory = SEARCH_PAGE_CATEGORIES.DEFAULT as SearchPageCategory,
) => {
  try {
    await dispatch(searchForContentNuggets({ search_term, page, types, category }));
  } catch (error) {
    console.error('Failed to fetch content nuggets:', error);
  }
};

type HandleInitiateSearchProps = Readonly<{
  dispatch: AppDispatch;
  currentSearchTerm: string;
  pageNum: number;
  items: SearchCategory[];
  searchPageCategory: SearchPageCategory;
  hasInitialSearchBeenMade?: boolean;
}>;

export const handleInitiateSearch = ({
  dispatch,
  currentSearchTerm,
  pageNum,
  items,
  searchPageCategory,
  hasInitialSearchBeenMade, // we assume the initial search has been made
}: HandleInitiateSearchProps) => {
  // Only initiate search if there's a search term OR if there are selected filter items
  if (
    (!currentSearchTerm || currentSearchTerm === '') &&
    (!items || items.length === 0) &&
    hasInitialSearchBeenMade
  ) {
    return;
  }

  dispatch(setSearchLoading(true));

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS) {
    const scholarshipPageSelectedItems = items?.length
      ? items
      : [
          !hasInitialSearchBeenMade
            ? SEARCH_CATEGORIES_SCHOLARSHIPS.PRE_FILTERED
            : ALL_SEARCH_CATEGORIES.SCHOLARSHIPS_SCHOLARSHIPS,
        ];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      scholarshipPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.INTERNSHIPS) {
    const nonItemSearch = !hasInitialSearchBeenMade
      ? [SEARCH_CATEGORIES_INTERNSHIPS.JOB_TYPE_ALL]
      : [
          ALL_SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_INTERNSHIPS,
          ALL_SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_JOBS,
        ];

    const internshipPageSelectedItems = items?.length ? items : nonItemSearch;

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      internshipPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.COLLEGES) {
    const universityPageSelectedItems = items?.length ? items : [ALL_SEARCH_CATEGORIES.COLLEGE_ALL];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      universityPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  fetchNuggetsUserHasSearched(
    dispatch,
    currentSearchTerm || '',
    pageNum,
    items,
    searchPageCategory,
  );
};

export function transformSearchResultsToNuggets(
  searchResult: SearchResult['results'],
): ContentNugget[] {
  const mappedResults: ContentNugget[] = (searchResult || [])
    .map(({ result }) => {
      switch (result.type) {
        case SEARCH_TYPES.COMPETENCY:
          return competencyTransform(result.competency);

        case SEARCH_TYPES.SCHOLARSHIP:
          return scholarshipTransform(result.scholarship);

        case SEARCH_TYPES.PROFILE:
          return profileTransform(result.profile);

        case SEARCH_TYPES.CAREER_PATH:
          return careerPathTransform(result.career_path);

        case SEARCH_TYPES.INTERNSHIP:
          return internshipTransform(result.internship);

        case SEARCH_TYPES.EXTERNAL_PROFILE:
          return externalProfileTransform(result.external_profile);

        case SEARCH_TYPES.COLLEGE:
          return collegeTransform(result.college);

        case SEARCH_TYPES.NUGGET:
          return {
            ...result.nugget,
          };

        default:
          return undefined;
      }
    })
    .filter((result) => !!result);

  return mappedResults;
}

export const calculateRange = (current: number, total: number): [number, number] => {
  if (current <= 4) {
    // if current page is 1, 2, 3, or 4, show pages 2, 3, 4, and 5
    return [2, Math.min(5, total - 1)];
  }

  if (current >= total - 3) {
    // if current page is the last 3 pages, show the last 3 pages
    return [Math.max(total - 4, 2), total - 1];
  }

  // if current page is in the middle, show the current page - 1 and current page + 1
  return [current - 1, current + 1];
};

/**
 * Returns the visible pages for the pagination component. Will be displayed like
 *
 * 1 ... 6 7 8 ... 200
 */
export const getVisiblePages = (current: number, total: number): PageButtonProps['display'][] => {
  if (total <= 1) {
    return [];
  }

  // first page is always shown
  const firstPage: PageButtonProps['display'][] = [{ type: 'PAGE_NUMBER', pageNumber: 1 }];

  // pre dots (if current page is greater than 4)
  const preDots: PageButtonProps['display'][] =
    current > 4 && total > 6 ? [{ type: 'ELLIPSIS' }] : [];

  // post dots (if current page is less than last page - 3)
  const postDots: PageButtonProps['display'][] =
    current < total - 3 && total > 6 ? [{ type: 'ELLIPSIS' }] : [];

  // last page is always shown
  const lastPage: PageButtonProps['display'][] =
    total > 1 ? [{ type: 'PAGE_NUMBER', pageNumber: total }] : [];

  // range numbers are what are dynamically shown
  const [rangeStart, rangeEnd] = calculateRange(current, total);
  const rangeNumbers = Array.from(
    { length: rangeEnd - rangeStart + 1 },
    (_, index) => rangeStart + index,
  );

  const rangeNumbersDisplay: PageButtonProps['display'][] = rangeNumbers.map((number) => ({
    type: 'PAGE_NUMBER',
    pageNumber: number,
  }));

  return [...firstPage, ...preDots, ...rangeNumbersDisplay, ...postDots, ...lastPage];
};
